import React, { useEffect, useMemo } from 'react';
import { useOrchestrationEvents } from '../hooks/useOrchestrationEvents';
import { useMsmeProfileQuery, useMsmeStatsQuery } from '../hooks/queries/useMsmeWorkspaceQueries';
import {
  useCarbonDashboardQuery,
  useCarbonSavingsQuery,
} from '../hooks/queries/useDashboardQueries';
import {
  buildDashboardBundle,
  type CarbonStatsPayload,
} from '../utils/dashboardQueryData';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import WorkspacePageHeader from './shared/WorkspacePageHeader';
import { useNavigate } from 'react-router-dom';
import { DashboardHero } from './dashboard/DashboardHero';
import DashboardWorkflowGuide from './dashboard/DashboardWorkflowGuide';
import MyCompanyRequiredNotice from './MyCompanyRequiredNotice';
import { resolveDashboardCarbonFields } from '../utils/carbonAnalyticsFields';
import { reconcileHeroCarbonTotalKg } from '../utils/reconcileHeroCarbonTotalKg';
import { hasDeclaredExportActivity } from '../utils/msmeExportIntent';
import { computeDataQualityScore } from '../utils/sustainabilityUx';
import { acceptPendingSupplierInviteIfAny } from '../utils/supplierInviteSession';

function getHotspotCopy(carbon: CarbonStatsPayload | undefined): string {
  const cat = carbon?.categoryBreakdown;
  if (cat && typeof cat === 'object') {
    const entries = Object.entries(cat)
      .map(([name, v]) => ({
        name,
        co2: Number((v as { co2Emissions?: number; co2?: number })?.co2Emissions ?? (v as { co2?: number })?.co2 ?? 0) || 0,
      }))
      .filter((e) => e.co2 > 0)
      .sort((a, b) => b.co2 - a.co2);
    if (entries.length > 0) {
      const labels = entries.slice(0, 3).map((e) => e.name.replace(/_/g, ' '));
      return `Highest-impact categories in this assessment: ${labels.join(', ')}.`;
    }
  }
  const esg = carbon?.esgScopes;
  if (!esg) return 'Complete a carbon assessment to see category hotspots.';
  const parts: { label: string; value: number }[] = [];
  const addBreak = (obj: Record<string, number> | undefined, prefix: string) => {
    if (!obj || typeof obj !== 'object') return;
    Object.entries(obj).forEach(([key, val]) => {
      if (typeof val === 'number' && val > 0) {
        const words = key.replace(/([A-Z])/g, ' $1').trim();
        parts.push({ label: `${prefix} — ${words}`, value: val });
      }
    });
  };
  addBreak(esg.scope1?.breakdown as Record<string, number> | undefined, 'Scope 1');
  addBreak(esg.scope2?.breakdown as Record<string, number> | undefined, 'Scope 2');
  addBreak(esg.scope3?.breakdown as Record<string, number> | undefined, 'Scope 3');
  if (parts.length === 0) {
    return 'Assessment data will highlight your highest-impact categories here.';
  }
  parts.sort((a, b) => b.value - a.value);
  const top = parts.slice(0, 3).map((p) => p.label);
  return `${top.join('; ')} — among the largest contributors in this assessment.`;
}

const Dashboard: React.FC = () => {
  useOrchestrationEvents(true);
  const navigate = useNavigate();

  const profileQuery = useMsmeProfileQuery();
  const statsQuery = useMsmeStatsQuery();
  const savingsQuery = useCarbonSavingsQuery();
  const carbonDashboardQuery = useCarbonDashboardQuery();

  const dashboardBundle = useMemo(
    () =>
      buildDashboardBundle({
        profileResponse: profileQuery.data,
        statsResponse: statsQuery.data,
        savingsResponse: savingsQuery.data,
        dashboardResponse: carbonDashboardQuery.data,
      }),
    [profileQuery.data, statsQuery.data, savingsQuery.data, carbonDashboardQuery.data]
  );

  const {
    msmeData,
    statsData,
    carbonSavings,
    additionalSavingsMetrics,
  } = dashboardBundle;

  const profilePending = profileQuery.isLoading && !profileQuery.data;
  const metricsPending =
    statsQuery.isLoading
    || savingsQuery.isLoading
    || carbonDashboardQuery.isLoading;

  const dashboardQueryError = useMemo(() => {
    const candidates = [
      profileQuery.error,
      statsQuery.error,
      carbonDashboardQuery.error,
    ];
    const first = candidates.find((err) => err instanceof Error);
    return first instanceof Error ? first.message : null;
  }, [profileQuery.error, statsQuery.error, carbonDashboardQuery.error]);

  const resolvedCarbonFields = useMemo(
    () => resolveDashboardCarbonFields(statsData?.carbon as CarbonStatsPayload | undefined),
    [statsData]
  );

  const carbonApi = statsData?.carbon as CarbonStatsPayload | undefined;
  const carbonScore = Number(statsData?.carbon?.currentScore || 0);
  const rawCarbonEmissionsKg = Number(statsData?.carbon?.totalCO2Emissions || 0);
  const esg = resolvedCarbonFields.esgScopes || carbonApi?.esgScopes;
  const s1Kg = Number(esg?.scope1?.total) || 0;
  const s2Kg = Number(esg?.scope2?.total) || 0;
  const s3Kg = Number(esg?.scope3?.total) || 0;
  const scopeSumKg = s1Kg + s2Kg + s3Kg;
  // CI-BUG-001: keep hero total aligned with scope bars when assessment total diverges.
  const carbonEmissionsKg = reconcileHeroCarbonTotalKg(rawCarbonEmissionsKg, scopeSumKg);
  const predictions = carbonApi?.predictions;
  const forecastKgFromModel =
    predictions && predictions.length > 0
      ? predictions.reduce((sum, p) => sum + (Number(p.predictedCO2) || 0), 0)
      : null;
  const forecastTCO2e =
    forecastKgFromModel != null && forecastKgFromModel > 0
      ? forecastKgFromModel / 1000
      : carbonEmissionsKg > 0
        ? carbonEmissionsKg / 1000
        : 0;
  const forecastCaption =
    forecastKgFromModel != null && forecastKgFromModel > 0
      ? 'Sum of modelled periods from your latest assessment'
      : carbonEmissionsKg > 0
        ? 'No modelled forecast is stored. This is the current inventory total.'
        : 'Complete an assessment to see a forecast';
  const hotspotDescription = getHotspotCopy({
    ...carbonApi,
    categoryBreakdown: resolvedCarbonFields.categoryBreakdown || carbonApi?.categoryBreakdown,
    esgScopes: esg,
  });
  const lastAssessmentLabel = carbonApi?.lastAssessment
    ? new Date(carbonApi.lastAssessment).toLocaleString()
    : null;
  const assessmentPeriodLabel =
    carbonApi?.assessmentPeriod?.startDate && carbonApi?.assessmentPeriod?.endDate
      ? `${new Date(carbonApi.assessmentPeriod.startDate).toLocaleDateString()} – ${new Date(
          carbonApi.assessmentPeriod.endDate
        ).toLocaleDateString()}`
      : null;

  const getCarbonScoreColor = (score: number): string => {
    if (score >= 80) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const getCarbonScoreLabel = (score: number): string => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Average';
    return 'Needs Improvement';
  };

  const getPerformanceLabel = (performance?: string): string => {
    switch (String(performance || '').toLowerCase()) {
      case 'excellent':
        return 'Excellent';
      case 'good':
        return 'Good';
      case 'average':
        return 'Average';
      case 'below_average':
        return 'Below Average';
      case 'poor':
        return 'Needs Improvement';
      case '':
      case 'undefined':
      case 'null':
        return 'Not Assessed';
      default:
        return performance || 'Not Assessed';
    }
  };

  /** Prefer score-band labels so Home matches Footprint/Profile (dossier DEEP-BUG-003 / 009). */
  const resolveHeroScoreLabel = (score: number, performance?: string): string => {
    const fromScore = getCarbonScoreLabel(score);
    const normalized = String(performance || '').toLowerCase();
    if (!normalized || normalized === 'undefined' || normalized === 'null') {
      return fromScore;
    }
    const fromPerf = getPerformanceLabel(performance);
    // Only overlay benchmark wording when it does not contradict the numeric band.
    const scoreIsStrong = score >= 60;
    const perfIsWeak = normalized === 'poor' || normalized === 'below_average';
    const scoreIsWeak = score < 40;
    const perfIsStrong = normalized === 'excellent' || normalized === 'good';
    if ((scoreIsStrong && perfIsWeak) || (scoreIsWeak && perfIsStrong)) {
      return fromScore;
    }
    return fromPerf || fromScore;
  };

  const isOnTrackStatus = (score: number, performance?: string): boolean => {
    const normalized = String(performance || '').toLowerCase();
    if (normalized === 'poor' || normalized === 'below_average') return false;
    if (normalized === 'excellent' || normalized === 'good') return true;
    return score >= 70;
  };

  useEffect(() => {
    void acceptPendingSupplierInviteIfAny();
  }, []);

  if (profilePending) {
    return (
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          px: 2,
          maxWidth: 520,
          mx: 'auto',
          textAlign: 'center',
        }}
      >
        <CircularProgress size={44} thickness={4} sx={{ mb: 2.5, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75, letterSpacing: '-0.02em' }}>
          Preparing your workspace
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          Pulling your company profile, transactions, and emissions signals into one view.
        </Typography>
        <LinearProgress sx={{ height: 6, borderRadius: 3, maxWidth: 400, mx: 'auto' }} />
      </Box>
    );
  }

  if (dashboardQueryError && !msmeData) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={(
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              void profileQuery.refetch();
              void statsQuery.refetch();
              void carbonDashboardQuery.refetch();
            }}
          >
            Retry
          </Button>
        )}
      >
        {dashboardQueryError}
      </Alert>
    );
  }

  if (!msmeData) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No MSME registration found. Please complete the registration first.
        <Button variant="contained" sx={{ ml: 2 }} onClick={() => navigate('/msme-registration')}>
          Register Now
        </Button>
      </Alert>
    );
  }

  const scoreColor = getCarbonScoreColor(carbonScore) as 'success' | 'warning' | 'error';

  const totalCo2SavedKg = Number(
    additionalSavingsMetrics?.totalCO2Saved ?? carbonSavings?.totalSavings ?? 0
  );
  const periodCo2SavedKg = Number(carbonSavings?.periodSavings ?? 0);
  const savingsPercentage = Number(carbonSavings?.savingsPercentage ?? 0);
  const costSavingsInr = Number(
    additionalSavingsMetrics?.costSavings ?? totalCo2SavedKg * 0.05
  );
  const treesEquivalent = Number(
    additionalSavingsMetrics?.environmentalImpact?.treesEquivalent ??
      (totalCo2SavedKg > 0 ? Math.round(totalCo2SavedKg / 22) : 0)
  );
  const greenCreditsAvailable = Number(statsData?.carbonCredits?.availableCredits ?? 0);
  const benchmarkPerformance = getPerformanceLabel(carbonSavings?.benchmarkComparison?.performance);

  const transactionCount = Math.max(
    Number(statsData?.transactions?.total ?? 0),
    Number((carbonDashboardQuery.data as { data?: { keyData?: { totalTransactions?: number }; totalTransactions?: number; totalAssessments?: number } } | undefined)?.data?.keyData?.totalTransactions ?? 0)
  );
  const dashboardPayload = (carbonDashboardQuery.data as { data?: { totalAssessments?: number; keyData?: { totalDocuments?: number } } } | undefined)?.data;
  const assessmentCount = Math.max(
    Number(statsData?.carbon?.totalAssessments ?? statsData?.carbon?.assessmentCount ?? statsData?.assessments?.total ?? 0),
    Number(dashboardPayload?.totalAssessments ?? 0)
  );
  const processedDocumentCount = Number(statsData?.documents?.processed ?? 0);
  const documentCount = processedDocumentCount > 0
    ? processedDocumentCount
    : Math.max(
      Number(statsData?.documentBulkUpload?.totalDocuments ?? 0),
      Number(statsData?.documents?.total ?? 0),
      Number(dashboardPayload?.keyData?.totalDocuments ?? 0)
    );
  const hasAssessment = carbonEmissionsKg > 0 || carbonScore > 0;
  const hasProfile = Boolean(msmeData?.companyName && msmeData?.gstNumber && hasDeclaredExportActivity(msmeData));
  const dataQuality = computeDataQualityScore({
    hasProfile,
    hasAssessment,
    carbonEmissionsKg,
    transactionCount,
    documentCount,
    hasScopeBreakdown: scopeSumKg > 0,
  });

  return (
    <Box>
      <WorkspacePageHeader
        title="Home"
        description={`Welcome back, ${msmeData.companyName}. Your carbon score, and the path through the workspace.`}
      />

      <MyCompanyRequiredNotice profile={msmeData} />

      {metricsPending ? (
        <Box component="section" aria-label="Performance" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
          <LinearProgress sx={{ height: 6, borderRadius: 3, mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            Loading your emissions summary…
          </Typography>
        </Box>
      ) : dashboardQueryError ? (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void statsQuery.refetch();
                void savingsQuery.refetch();
                void carbonDashboardQuery.refetch();
              }}
            >
              Retry
            </Button>
          )}
        >
          {dashboardQueryError}
        </Alert>
      ) : (
      <>
      <Box component="section" aria-label="Performance" sx={{ mb: 3 }}>
        <DashboardHero
          carbonScore={carbonScore}
          scoreLabel={resolveHeroScoreLabel(carbonScore, carbonSavings?.benchmarkComparison?.performance)}
          onTrack={isOnTrackStatus(carbonScore, carbonSavings?.benchmarkComparison?.performance)}
          scoreColor={scoreColor}
          carbonEmissionsKg={carbonEmissionsKg}
          s1Kg={s1Kg}
          s2Kg={s2Kg}
          s3Kg={s3Kg}
          scopeSumKg={scopeSumKg}
          lastAssessmentLabel={lastAssessmentLabel}
          assessmentPeriodLabel={assessmentPeriodLabel}
          forecastTCO2e={forecastTCO2e}
          forecastCaption={forecastCaption}
          hotspotDescription={hotspotDescription}
          dataQuality={{
            level: dataQuality.level,
            label: dataQuality.label,
            score: dataQuality.score,
            hints: dataQuality.hints,
          }}
          msmeHighlights={{
            transactionCount,
            documentCount,
            assessmentCount,
            greenCreditsAvailable,
            totalCo2SavedKg,
            periodCo2SavedKg,
            savingsPercentage,
            costSavingsInr,
            treesEquivalent,
            benchmarkPerformance,
            companyType: msmeData.companyType,
            industry: msmeData.industry,
            employeeCount: msmeData.business?.numberOfEmployees,
          }}
        />
      </Box>

      <DashboardWorkflowGuide />
      </>
      )}
    </Box>
  );
};

export default Dashboard;
